import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import moment from 'moment';
import SelectOptionModal from '~/Components/SelectOptionModal';
import { range } from 'lodash';
import PeopleIcon from '~/Assets/icons/people_alt.svg';
import TimeIcon from '~/Assets/icons/schedule.svg';

import { useI18n, useSelector } from '~/Hooks';
import TableNote from './TableNote';
import { useBookings } from '~Providers/BookingsProvider';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  background: #2f353d;
  padding: 16px;
  box-sizing: border-box;
  position: relative;
  gap: 8px;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
`;

const InfoTitle = styled.div`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  margin-right: auto;
  width: 100%;
  text-align: center;
`;

const Info = styled.div`
  color: #c3c5c7;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const Icon = styled.span<{
  icon: string;
}>`
  height: 24px;
  width: 24px;
  mask-image: url(${(props) => props.icon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-right: 4px;
  background-color: #545960;
`;

const Nr = styled.span`
  margin-right: 4px;
`;

const BookingInfoView = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  border: 1px solid rgba(118, 75, 162, 0.4);
  border-radius: 4px;
  padding: 6px 12px;
  gap: 2px;
`;

const BookingInfoText = styled.div`
  color: #c3c5c7;
  font-size: 13px;
  font-weight: 600;
  word-break: break-word;
`;

const BookingInfoName = styled.span`
  color: #fff;
`;

const DisplayNoteView = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  border: 1px solid #545960;
  border-radius: 4px;
  padding-bottom: 2px;
`;

const DisplayNoteText = styled.div`
  color: #c3c5c7;
  padding: 0 16px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface Props {
  selectedTable: { id: string; name: string };
  allCheques: any;
  selectedCheque: string | null;
  showLogin: () => void;
}

const TableInfo: React.FunctionComponent<Props> = ({
  selectedTable,
  allCheques,
  selectedCheque,
  showLogin,
}) => {
  const { i18n } = useI18n();
  const [isEditingNote, setIsEditingNote] = useState(false);
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );
  const [showModal, setShowModal] = useState(false);
  const api = useTableApi();
  const tableCheques = allCheques.filter((item: any) => item.tableId === selectedTable.id);
  const nrOfPeople = Math.max(
    ...tableCheques.map((item: any) => (item.coverage ? item.coverage : 0)),
  );
  const [time, setTime] = useState(moment.duration());
  const [timeCounter, setTimeCounter] = useState(0);

  const updateTime = () => {
    if (tableCheques.length < 1) return;

    const oldestCheque = tableCheques.reduce((itemA: any, itemB: any) =>
      itemB.createdAt < itemA.createdAt ? itemB : itemA,
    );

    setTime(
      moment.duration(
        moment()
          .startOf('minutes')
          .diff(moment(oldestCheque.createdAt).startOf('minutes')),
      ),
    );
  };

  // Updates the time on interval and och selected cheque changed
  useEffect(updateTime, [timeCounter, selectedCheque, selectedTable]);

  // Interval every minute
  useEffect(() => {
    const intervalId = setInterval(() => setTimeCounter((counter) => counter + 1), 60000);
    return () => clearInterval(intervalId);
  }, [selectedCheque]);

  // Change coverage for all cheque on the selected table
  const changeCoverage = (count: number) => {
    tableCheques.map((cheque: any) => {
      api.setChequeCoverage(cheque.id, count);
      api.log.setChequeCoverage(cheque.id, count);
    });
    setShowModal(false);
  };

  const universe = useTableUniverse();
  const note = universe?.notes ? universe?.notes[selectedTable.id] : null;
  const bookings = useBookings();

  // Get booking data for this table: prefer backend booking, fall back to cheque.bookingData
  const bookingInfo = useMemo(() => {
    // First try backend booking data (started bookings linked to cheques on this table)
    const chequeIds = tableCheques.map((c: any) => c.id);
    const backendBooking = bookings.today.find(
      (b) => b.status === 'STARTED' && chequeIds.includes(b.chequeId),
    );

    if (backendBooking) {
      const chequeWithBooking = tableCheques.find(
        (c: any) => c.id === backendBooking.chequeId && c.bookingData,
      );
      return {
        guestName: backendBooking.guestName,
        startTime: backendBooking.startTime,
        endsAt: chequeWithBooking?.bookingData?.endsAt || null,
        endTime: backendBooking.endTime,
        comment: backendBooking.comment,
      };
    }

    // Fall back to cheque.bookingData
    const chequeWithBooking = tableCheques.find((c: any) => c.bookingData);
    if (chequeWithBooking?.bookingData) {
      const bd = chequeWithBooking.bookingData;
      const name = [bd.firstName, bd.lastName].filter(Boolean).join(' ').trim();
      return {
        guestName: name || null,
        startTime: chequeWithBooking.createdAt
          ? moment(chequeWithBooking.createdAt).format('HH:mm')
          : null,
        endsAt: bd.endsAt || null,
        endTime: null,
        comment: bd.comment || null,
      };
    }

    return null;
  }, [tableCheques, bookings.today]);

  return (
    <Wrapper>
      <InfoTitle>{i18n('TM.Table') + ' #' + selectedTable.name}</InfoTitle>
      <Container>
        <Info onClick={() => (staffId !== null ? setShowModal(true) : showLogin())}>
          <Icon icon={PeopleIcon} />
          <Nr>{tableCheques.length > 0 && nrOfPeople > 0 ? nrOfPeople : ' - '}</Nr>
        </Info>
        <Info>
          <Icon icon={TimeIcon} />
          {time && tableCheques.length > 0
            ? time.hours() + 'h ' + time.minutes() + i18n('TSR.Min')
            : ' - '}
        </Info>
        <TableNote
          tableId={selectedTable.id}
          isEditing={isEditingNote}
          setIsEditing={setIsEditingNote}
        />
      </Container>

      {showModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TSR.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => changeCoverage(count as number)}
          onCancel={() => setShowModal(false)}
        />
      )}

      {bookingInfo && (() => {
        const endTimeStr = bookingInfo.endsAt
          ? moment(bookingInfo.endsAt).format('HH:mm')
          : bookingInfo.endTime || null;
        const timeStr = endTimeStr
          ? bookingInfo.startTime
            ? `${bookingInfo.startTime} - ${endTimeStr}`
            : endTimeStr
          : null;

        return (
          <BookingInfoView>
            {bookingInfo.comment ? (
              <>
                {bookingInfo.guestName && (
                  <BookingInfoText>
                    <BookingInfoName>{bookingInfo.guestName}</BookingInfoName>
                  </BookingInfoText>
                )}
                {timeStr && <BookingInfoText>{timeStr}</BookingInfoText>}
                <BookingInfoText>{bookingInfo.comment}</BookingInfoText>
              </>
            ) : (
              <BookingInfoText>
                {bookingInfo.guestName && (
                  <BookingInfoName>{bookingInfo.guestName}</BookingInfoName>
                )}
                {bookingInfo.guestName && timeStr && ' · '}
                {timeStr}
              </BookingInfoText>
            )}
          </BookingInfoView>
        );
      })()}

      {note && (
        <DisplayNoteView onClick={() => setIsEditingNote(true)}>
          <DisplayNoteText>{note?.text}</DisplayNoteText>
        </DisplayNoteView>
      )}
    </Wrapper>
  );
};

export default React.memo(TableInfo);
